import { LitElement, html } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import _ from 'lodash';
import { translateFn } from '../../../utils/translator.helper';
import { MitreTechnique } from '../../suspectedActorsCard/types';
import { GzSelectWebComponent } from '../../../ui/gzSelect';
import { SelectEvents } from '../../../ui/gzSelect/types';
import { WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { INLINE_ACTIONS } from '../types';

/**
 * @summary Type here the description of this component
 * @tag group-attribution-mitre-side-panel
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-group-attribution-mitre-side-panel')
export class GroupAttributionMitreSidePanelWebComponent extends LitElement {
  /**
   * See https://lit-element.polymer-project.org/guide/properties
   * Write here the description of this field
   */
  @property({
    type: Array,
    attribute: true,
    reflect: true,
  })
  _data: MitreTechnique[];

  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  companyId = null;

  @state()
  private _currentData;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  get data() {
    return this._data;
  }

  set data(prop) {
    const oldData = this._data;
    this._data = prop;
    this._currentData = _.cloneDeep(this._data);
    this.requestUpdate('data', oldData);
  }

  render() {
    return html`
      <div class="header pb-3 pr-2">
        <gz-tooltip class="mb-2" tooltipText="${translateFn('MITRE Techniques')}" isEllipsis>
          <gz-title class="d-inline" fontsize="16" fontWeight="500">${translateFn('MITRE Techniques')}</gz-title>
        </gz-tooltip>

        <gz-input-search
          placeholder="${translateFn('Search MITRE Techniques')}"
          @change="${this.handleInputChange}"
        ></gz-input-search>
      </div>
      <div class="content">
        <gz-labeled-content
          label="${translateFn('Technique ID') + ' (' + this.data.length + ')'}"
          labelFontSize="11"
          labelFontWeight="500"
        >
          <gz-text fontSize="11" fontWeight="500" class="d-inline subtitle-value"
          >${translateFn('Technique name')}
          </gz-text
          >
        </gz-labeled-content>
        ${this.renderMitreTechniques()}
      </div>
    `;
  }

  handleInputChange(event) {
    const searchInput = event.detail.sourceEvent.trim().toLowerCase();
    this._currentData = this.data.filter(
      mitre => mitre.id.toLowerCase().includes(searchInput) || mitre.name.toLowerCase().includes(searchInput),
    );
  }

  private formatMitreId(mitreId: string) {
    return mitreId.replace('.', '/');
  }

  private getSelectOptions() {
    return [
      {
        title: translateFn('View events and alerts'),
        value: INLINE_ACTIONS.SEARCH,
      },
      {
        title: translateFn('View in Incidents'),
        value: INLINE_ACTIONS.INCIDENTS,
      },
    ];
  }

  private getSelectTemplate() {
    return `<gz-icon-button icon="ds-icon-more" buttoncolor="secondary" buttontype="square"
        fontsize="22" size="24" buttonfill="quiet" class="inline-menu-trigger"></gz-icon-button>`;
  }

  renderInlineButton(mitreId: string, mitreName: string) {
    const select = document.createElement('gz-select') as GzSelectWebComponent;
    select.options = this.getSelectOptions();
    select.innerHTML = this.getSelectTemplate();
    select.addEventListener(SelectEvents.SELECTION_CHANGED, async (event: WebComponentEvent) => {
      if (event.detail.selected === INLINE_ACTIONS.SEARCH) {
        this.handlePivotToAdvancedSearch(mitreId, mitreName);
      }
      if (event.detail.selected === INLINE_ACTIONS.INCIDENTS) {
        this.handlePivotToIncidents(mitreId, mitreName);
      }
    });
    return select;
  }

  private checkIfSubtechnique(mitreId: string) {
    return mitreId.includes('.');
  }

  private handlePivotToAdvancedSearch(mitreId: string, mitreName: string) {
    if (!this.checkIfSubtechnique(mitreId)) {
      Application.lib.Navigation.openInNewTab(`/xdr/events/technique/${this.companyId}&${encodeURIComponent(mitreName)}`);
    } else {
      Application.lib.Navigation.openInNewTab(`/xdr/events/subtechnique/${this.companyId}&${encodeURIComponent(mitreName)}`);
    }
  }

  private handlePivotToIncidents(mitreId: string, mitreName: string) {
    if (!this.checkIfSubtechnique(mitreId)) {
      Application.lib.Navigation.openInNewTab(`/incidents/filter/technique/${this.companyId}&${encodeURIComponent(mitreName)}`);
    } else {
      Application.lib.Navigation.openInNewTab(`/incidents/filter/subtechnique/${this.companyId}&${encodeURIComponent(mitreName)}`);
    }
  }

  renderMitreTechniques() {
    return this._currentData.map(
      mitre => html`
        <div class="container-mitre d-flex flex-column">
          <div class="d-flex flex-row">
            <gz-link class="label" fontSize="12" href="${'https://attack.mitre.org/techniques/' + this.formatMitreId(mitre.id)}">
              <gz-text class="d-inline" fontSize="12">${mitre.id}</gz-text>
            </gz-link>
            <gz-tooltip class="value" tooltipText="${mitre.name}" isEllipsis>
              <gz-text class="d-inline" fontSize="12">${mitre.name}</gz-text>
            </gz-tooltip>
            ${this.renderInlineButton(mitre.id, mitre.name)}
          </div>
        </div>
      `,
    );
  }

  constructor() {
    super();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'group-attribution-mitre-side-panel': GroupAttributionMitreSidePanelWebComponent;
  }
}
